/**
 * Sincroniza rascunhos de pré-venda pendentes com o servidor (reserva estoque).
 */

import { saleService } from './erpApi';
import {
  isNetworkFailure,
  listPendingDrafts,
  markDraftError,
  markDraftSynced,
  putDraft,
  type PresaleDraft,
  type PresaleDraftItem,
} from './presaleDraftDb';

function calcItemTotals(product: PresaleDraftItem) {
  const quantity = Number(product.quantity) || 0;
  const unitPrice = Number(product.unitPrice) || 0;
  const baseTotal = quantity * unitPrice;

  let discountAmount = 0;
  if (product.discount && product.discount > 0) {
    if (product.discountType === 'percent') {
      discountAmount = baseTotal * (Number(product.discount) / 100);
    } else {
      discountAmount = Number(product.discount);
    }
  }

  let surchargeAmount = 0;
  if (product.surcharge && product.surcharge > 0) {
    if (product.surchargeType === 'percent') {
      surchargeAmount = baseTotal * (Number(product.surcharge) / 100);
    } else {
      surchargeAmount = Number(product.surcharge);
    }
  }

  return {
    baseTotal,
    discountAmount,
    surchargeAmount,
    finalTotal: baseTotal - discountAmount + surchargeAmount,
  };
}

async function syncSaleItems(saleId: number, items: PresaleDraftItem[]) {
  const saleResponse = await saleService.getById(saleId);
  const existingItems = saleResponse.data?.items || [];

  for (const item of existingItems) {
    await saleService.removeItem(saleId, item.id);
  }

  for (const product of items) {
    const calc = calcItemTotals(product);
    const discountPercent = product.discountType === 'percent' ? product.discount : 0;

    await saleService.addItem(saleId, {
      productId: product.id,
      quantity: product.quantity,
      unitPrice: product.unitPrice,
      discountPercent: discountPercent || 0,
      discountAmount: calc.discountAmount || 0,
      totalPrice: calc.finalTotal,
    });
  }
}

export async function pushPresaleDraftToServer(draft: PresaleDraft): Promise<{ saleId: number }> {
  if (!draft.items?.length) {
    throw new Error('Rascunho sem itens');
  }
  if (!draft.sellerId) {
    throw new Error('Rascunho sem vendedor');
  }

  const subtotal =
    draft.totals?.subtotal ??
    draft.items.reduce((acc, item) => acc + calcItemTotals(item).baseTotal, 0);
  const discountAmount =
    draft.totals?.discount ??
    draft.items.reduce((acc, item) => acc + calcItemTotals(item).discountAmount, 0);
  const taxAmount =
    draft.totals?.surcharge ??
    draft.items.reduce((acc, item) => acc + calcItemTotals(item).surchargeAmount, 0);
  const finalAmount = draft.totals?.final ?? subtotal - discountAmount + taxAmount;

  let saleId: number | null = draft.importedSaleId || null;

  if (draft.importedQuoteId && !saleId) {
    const fromQuoteResponse = await saleService.createFromQuote(draft.importedQuoteId, {
      type: 'presale',
    });
    if (!fromQuoteResponse.success || !fromQuoteResponse.data?.sale?.id) {
      throw new Error(fromQuoteResponse.message || 'Erro ao converter orçamento em pré-venda');
    }
    saleId = fromQuoteResponse.data.sale.id;
  }

  if (saleId) {
    const updateResponse = await saleService.update(saleId, {
      customerId: draft.customerId || null,
      saleDate: new Date().toISOString(),
      totalAmount: subtotal,
      discountAmount,
      taxAmount,
      finalAmount,
      observation: draft.observations || '',
      sellerId: draft.sellerId || null,
    });
    if (!updateResponse.success) {
      throw new Error(updateResponse.message || 'Erro ao atualizar pré-venda');
    }
    await syncSaleItems(saleId, draft.items);
  } else {
    const createResponse = await saleService.create({
      type: 'presale',
      customerId: draft.customerId || null,
      saleDate: new Date().toISOString(),
      totalAmount: subtotal,
      discountAmount,
      taxAmount,
      finalAmount,
      status: 'draft',
      observation: draft.observations || '',
      sellerId: draft.sellerId || null,
    });
    if (!createResponse.success) {
      throw new Error(createResponse.message || 'Erro ao criar pré-venda');
    }
    saleId = createResponse.data.id;
    await syncSaleItems(saleId, draft.items);
  }

  const finalizeResponse = await saleService.finalizePresale(saleId);
  if (!finalizeResponse.success) {
    throw new Error(finalizeResponse.message || 'Erro ao finalizar pré-venda');
  }

  return { saleId };
}

export type SyncDraftErrorKind = 'network' | 'business';

export async function syncPendingPresaleDrafts(userId: string): Promise<{
  synced: number;
  failed: number;
  networkBlocked: number;
  errors: Array<{ draftId: string; message: string; kind: SyncDraftErrorKind }>;
}> {
  const pending = await listPendingDrafts(userId);
  let synced = 0;
  let failed = 0;
  let networkBlocked = 0;
  const errors: Array<{ draftId: string; message: string; kind: SyncDraftErrorKind }> = [];

  for (const draft of pending) {
    try {
      await pushPresaleDraftToServer(draft);
      await markDraftSynced(draft.id);
      synced += 1;
    } catch (err: unknown) {
      const maybeErr = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const message =
        maybeErr?.response?.data?.message ||
        maybeErr?.message ||
        'Erro ao sincronizar pré-venda';

      // Servidor inacessível: mantém na fila pending (não é conflito de estoque)
      if (isNetworkFailure(err)) {
        networkBlocked += 1;
        await putDraft({
          ...draft,
          syncStatus: 'pending',
          syncError: null,
          updatedAt: Date.now(),
        });
        errors.push({ draftId: draft.id, message, kind: 'network' });
        break;
      }

      failed += 1;
      await markDraftError(draft.id, message);
      errors.push({ draftId: draft.id, message, kind: 'business' });
    }
  }

  return { synced, failed, networkBlocked, errors };
}
